import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import emailjs from '@emailjs/browser';
import { 
  Smartphone, 
  Globe, 
  Code2, 
  Lightbulb, 
  ArrowRight, 
  Mail, 
  Phone, 
  MapPin,
  Star,
  Zap,
  Shield,
  Users,
  CheckCircle,
  Send
} from "lucide-react";
import "@/App.css";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Animated Counter Component
const AnimatedCounter = ({ target, suffix = "", duration = 2, startAnimation = false }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!startAnimation) return;
    
    let startTime;
    const startValue = 0;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * target);
      
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [startAnimation, target, duration]);
  
  return (
    <span>
      {count}{suffix}
    </span>
  );
};

// Stats Section Component
const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  const stats = [
    { value: 150, suffix: "+", label: "Proyectos" },
    { value: 50, suffix: "+", label: "Clientes" },
    { value: 5, suffix: "+", label: "Años" }
  ];

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      className="mt-16 grid grid-cols-3 gap-8 max-w-lg"
    >
      {stats.map((stat, i) => (
        <div key={i} className="text-center sm:text-left">
          <div className="text-3xl md:text-4xl font-bold gradient-text">
            <AnimatedCounter 
              target={stat.value} 
              suffix={stat.suffix} 
              duration={4.5} 
              startAnimation={isInView}
            />
          </div>
          <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
        </div>
      ))}
    </motion.div>
  );
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navbar Component
const Navbar = () => {
  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 px-3 md:px-6 py-3 md:py-4"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto">
        <div className="glass-card px-3 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
          <a href="#hero" className="flex items-center gap-2 md:gap-3 flex-shrink-0" data-testid="logo-link">
            {/* Professional Logo */}
            <div className="w-9 h-9 md:w-11 md:h-11 rounded-lg md:rounded-xl bg-gradient-to-br from-purple-600 via-purple-500 to-cyan-400 p-[2px]">
              <div className="w-full h-full rounded-[6px] md:rounded-[10px] bg-[#0a0a1a] flex items-center justify-center">
                <div className="relative">
                  <Globe className="w-4 h-4 md:w-5 md:h-5 text-cyan-400" />
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-500 rounded-full" />
                </div>
              </div>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-bold text-sm md:text-lg tracking-tight">INNOVA <span className="text-purple-400">APP</span></span>
              <span className="text-[8px] md:text-[10px] font-medium tracking-[0.15em] md:tracking-[0.2em] text-cyan-400 mt-0.5">SOLUTIONS</span>
            </div>
          </a>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#servicios" className="text-slate-300 hover:text-white transition-colors" data-testid="nav-services">Servicios</a>
            <a href="#portfolio" className="text-slate-300 hover:text-white transition-colors" data-testid="nav-portfolio">Portfolio</a>
            <a href="#nosotros" className="text-slate-300 hover:text-white transition-colors" data-testid="nav-about">Nosotros</a>
            <a href="#contacto" className="text-slate-300 hover:text-white transition-colors" data-testid="nav-contact">Contacto</a>
          </div>

          <a href="https://wa.me/573107398319?text=Hola%2C%20me%20interesa%20conocer%20más%20sobre%20sus%20servicios" target="_blank" rel="noopener noreferrer" className="btn-primary text-xs md:text-sm px-3 py-2 md:px-6 md:py-3 flex-shrink-0" data-testid="nav-cta">
            <span className="hidden sm:inline">Iniciar Proyecto</span>
            <span className="sm:hidden">Contacto</span>
          </a>
        </div>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section 
      ref={ref}
      id="hero" 
      className="min-h-screen relative overflow-hidden pt-32 pb-20 md:pt-48 md:pb-32"
      data-testid="hero-section"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Animated Grid */}
        <div className="absolute inset-0 grid-animated opacity-20" />
        
        {/* Floating Orbs - Hidden on mobile for performance */}
        <div className="hidden md:block">
          <div className="orb orb-1" />
          <div className="orb orb-2" />
          <div className="orb orb-3" />
        </div>
        
        {/* Rotating Globe Effect - Hidden on mobile */}
        <div className="absolute top-1/2 right-[10%] -translate-y-1/2 w-[500px] h-[500px] md:w-[700px] md:h-[700px] opacity-30 hidden md:block">
          <div className="globe-container">
            <div className="globe" />
            <div className="globe-ring globe-ring-1" />
            <div className="globe-ring globe-ring-2" />
            <div className="globe-ring globe-ring-3" />
          </div>
        </div>
        
        {/* Particles - Reduced for performance */}
        <div className="particles hidden md:block">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }} />
          ))}
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#030014]/60 via-[#030014]/40 to-[#030014]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030014]/90 via-transparent to-[#030014]/70" />
      </div>

      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-purple-500/30 rounded-full blur-[120px] pointer-events-none z-[1]" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-cyan-500/20 rounded-full blur-[100px] pointer-events-none z-[1]" />
      
      {/* Android with Globe - Main Hero Image */}
      <motion.div 
        style={{ y }}
        className="absolute top-16 right-0 w-[280px] h-[350px] sm:w-[350px] sm:h-[450px] md:w-[500px] md:h-[600px] lg:w-[650px] lg:h-[750px] z-[3] overflow-hidden"
        data-testid="hero-android"
      >
        <div className="android-container relative w-full h-full">
          {/* Android Image */}
          <img 
            src="https://customer-assets.emergentagent.com/job_hola-hello-305/artifacts/l6h6khok_Gemini_Generated_Image_a9o3axa9o3axa9o3.png"
            alt="AI Android with Holographic Globe"
            className="w-full h-full object-contain object-right android-float"
            style={{ 
              filter: 'drop-shadow(0 0 40px rgba(6, 182, 212, 0.5)) drop-shadow(0 0 80px rgba(168, 85, 247, 0.3))',
              maskImage: 'linear-gradient(to left, black 70%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to left, black 70%, transparent 100%)'
            }}
          />
          
          {/* Animated Globe Glow Overlay */}
          <div className="globe-glow-overlay hidden md:block" />
          
          {/* Rotating Ring around Globe area */}
          <div className="globe-rotate-ring hidden md:block" />
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div 
          style={{ opacity }}
          className="max-w-4xl"
        >
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-sm font-mono tracking-widest text-purple-400 mb-6 uppercase"
          >
            Desarrollo de Aplicaciones Premium
          </motion.p>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-none mb-8"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <span className="block">Transformamos</span>
            <span className="block gradient-text">Ideas en Apps</span>
            <span className="block">Extraordinarias</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg md:text-xl text-slate-400 max-w-2xl mb-10 leading-relaxed"
          >
            En <strong className="text-white">INNOVA APP SOLUTIONS</strong>, creamos experiencias digitales 
            que impulsan tu negocio hacia el futuro. Desarrollo móvil, web y software a medida.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a href="#contacto" className="btn-primary flex items-center justify-center gap-2" data-testid="hero-cta-primary">
              <span>Empezar Proyecto</span>
              <ArrowRight className="w-5 h-5" />
            </a>
            <a href="#portfolio" className="btn-secondary flex items-center justify-center gap-2" data-testid="hero-cta-secondary">
              <span>Ver Portfolio</span>
            </a>
          </motion.div>

          {/* Stats */}
          <StatsSection />
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const services = [
  {
    icon: Smartphone,
    title: "Apps Móviles",
    description: "Aplicaciones nativas y multiplataforma para iOS y Android con interfaces modernas.",
    gradient: "from-purple-500 to-pink-500",
    span: "md:col-span-2"
  },
  {
    icon: Globe,
    title: "Desarrollo Web",
    description: "Sitios web y aplicaciones web progresivas de alto rendimiento.",
    gradient: "from-cyan-500 to-blue-500",
    span: ""
  },
  {
    icon: Code2,
    title: "Software a Medida",
    description: "Soluciones personalizadas que se adaptan a las necesidades específicas de tu negocio.",
    gradient: "from-green-500 to-emerald-500",
    span: ""
  },
  {
    icon: Lightbulb,
    title: "Consultoría Tech",
    description: "Asesoramiento estratégico para transformar digitalmente tu empresa.",
    gradient: "from-orange-500 to-amber-500",
    span: "md:col-span-2"
  }
];

const ServicesSection = () => {
  return (
    <section id="servicios" className="py-32 relative" data-testid="services-section">
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <AnimatedSection>
          <motion.p variants={fadeInUp} className="text-sm font-mono tracking-widest text-cyan-400 mb-4 uppercase">
            Nuestros Servicios
          </motion.p>
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl md:text-6xl font-bold tracking-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Soluciones <span className="gradient-text">Digitales</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-slate-400 text-lg max-w-2xl mb-16">
            Ofrecemos servicios integrales de desarrollo tecnológico para llevar tu negocio al siguiente nivel.
          </motion.p>
        </AnimatedSection>

        <AnimatedSection className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={i}
              variants={scaleIn}
              className={`glass-card p-8 group cursor-pointer perspective-container ${service.span}`}
              data-testid={`service-card-${i}`}
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <service.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 group-hover:text-purple-300 transition-colors">{service.title}</h3>
              <p className="text-slate-400 leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
};

// Portfolio Section
const projects = [
  {
    title: "FactuYa!",
    category: "App Móvil + Web",
    description: "La app de facturación más fácil para negocios en Latinoamérica. Crea facturas profesionales en segundos.",
    image: "https://static.prod-images.emergentagent.com/jobs/380f7905-e22e-4890-bf32-ad048c328c8c/images/cbf8c9555a7e26db470683536696b0a4fbbc534b686234bd784866245496b92b.png",
    color: "purple"
  },
  {
    title: "HealthCare Pro",
    category: "Plataforma Web",
    description: "Sistema de gestión hospitalaria con telemedicina integrada.",
    image: "https://images.unsplash.com/photo-1663153203126-08bbadc178ad?w=800&auto=format&fit=crop",
    color: "cyan"
  },
  {
    title: "E-Commerce Plus",
    category: "App Móvil + Web",
    description: "Marketplace con IA para recomendaciones personalizadas.",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&auto=format&fit=crop",
    color: "green"
  }
];

const PortfolioSection = () => {
  return (
    <section id="portfolio" className="py-32 relative overflow-hidden" data-testid="portfolio-section">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <AnimatedSection>
          <motion.p variants={fadeInUp} className="text-sm font-mono tracking-widest text-purple-400 mb-4 uppercase">
            Portfolio
          </motion.p>
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl md:text-6xl font-bold tracking-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Proyectos <span className="gradient-text">Destacados</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-slate-400 text-lg max-w-2xl mb-16">
            Descubre algunos de los proyectos que hemos desarrollado para empresas líderes en distintas industrias.
          </motion.p>
        </AnimatedSection>

        <div className="space-y-20">
          {projects.map((project, i) => (
            <AnimatedSection key={i}>
              <motion.div 
                variants={fadeInUp}
                className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
                data-testid={`portfolio-item-${i}`}
              >
                <div className={`${i % 2 === 1 ? 'md:order-2' : ''}`}>
                  <span className={`text-sm font-mono tracking-wider ${project.color === 'purple' ? 'text-purple-400' : project.color === 'cyan' ? 'text-cyan-400' : 'text-green-400'} uppercase`}>
                    {project.category}
                  </span>
                  <h3 className="text-3xl md:text-4xl font-bold mt-2 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {project.title}
                  </h3>
                  <p className="text-slate-400 text-lg leading-relaxed mb-6">
                    {project.description}
                  </p>
                  <button className="btn-secondary inline-flex items-center gap-2">
                    <span>Ver Caso de Estudio</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                
                <div className={`${i % 2 === 1 ? 'md:order-1' : ''}`}>
                  <div className="glass-card p-4 group">
                    <div className="overflow-hidden rounded-xl">
                      <img 
                        src={project.image} 
                        alt={project.title}
                        className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const features = [
    { icon: Shield, text: "Código de Alta Calidad" },
    { icon: Zap, text: "Entregas Rápidas" },
    { icon: Users, text: "Soporte 24/7" },
    { icon: CheckCircle, text: "Garantía de Satisfacción" }
  ];

  return (
    <section id="nosotros" className="py-32 relative" data-testid="about-section">
      <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-green-500/10 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <motion.div variants={fadeInUp} className="glass-card p-4">
              <img 
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&auto=format&fit=crop" 
                alt="Equipo INNOVA APP SOLUTIONS"
                className="w-full h-80 md:h-[450px] object-cover rounded-xl"
              />
            </motion.div>
          </AnimatedSection>

          <AnimatedSection>
            <motion.p variants={fadeInUp} className="text-sm font-mono tracking-widest text-green-400 mb-4 uppercase">
              Sobre Nosotros
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Innovación que <span className="gradient-text">Transforma</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-400 text-lg leading-relaxed mb-8">
              Somos un equipo apasionado de desarrolladores, diseñadores y estrategas digitales 
              comprometidos con crear soluciones tecnológicas que impulsen el crecimiento de tu negocio.
              Con más de 5 años de experiencia, hemos ayudado a más de 50 empresas a alcanzar sus metas digitales.
            </motion.p>

            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4 mb-8">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-3 glass-card p-4">
                  <feature.icon className="w-5 h-5 text-purple-400" />
                  <span className="text-sm text-slate-300">{feature.text}</span>
                </div>
              ))}
            </motion.div>

            <motion.a 
              variants={fadeInUp}
              href="#contacto" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="about-cta"
            >
              <span>Trabajemos Juntos</span>
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const testimonials = [
  {
    name: "María González",
    role: "CEO, TechStart",
    content: "INNOVA APP SOLUTIONS transformó completamente nuestra presencia digital. Su equipo es excepcional.",
    avatar: "https://images.unsplash.com/photo-1712863132626-60bb701a6f4a?w=200&auto=format&fit=crop"
  },
  {
    name: "Carlos Mendoza",
    role: "Fundador, FinApp",
    content: "El mejor equipo de desarrollo con el que hemos trabajado. Entregaron a tiempo y superaron expectativas.",
    avatar: "https://images.unsplash.com/photo-1599132880549-bffe2907f808?w=200&auto=format&fit=crop"
  },
  {
    name: "Ana Rodríguez",
    role: "CTO, HealthTech",
    content: "Profesionales de primer nivel. Nuestra app ha recibido excelentes reseñas gracias a su trabajo.",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&auto=format&fit=crop"
  }
];

const TestimonialsSection = () => {
  return (
    <section className="py-32 relative" data-testid="testimonials-section">
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <AnimatedSection className="text-center mb-16">
          <motion.p variants={fadeInUp} className="text-sm font-mono tracking-widest text-cyan-400 mb-4 uppercase">
            Testimonios
          </motion.p>
          <motion.h2 
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Lo que dicen <span className="gradient-text">nuestros clientes</span>
          </motion.h2>
        </AnimatedSection>

        <AnimatedSection className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <motion.div 
              key={i}
              variants={scaleIn}
              className="glass-card p-8"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <p className="text-slate-300 leading-relaxed mb-6">"{testimonial.content}"</p>
              <div className="flex items-center gap-4">
                <img 
                  src={testimonial.avatar} 
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-purple-500/50"
                />
                <div>
                  <div className="font-semibold text-white">{testimonial.name}</div>
                  <div className="text-sm text-slate-500">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      await emailjs.sendForm(
        'service_ohiykzc',
        'template_ap97o7o',
        formRef.current,
        'Ev6HtYe6cBw1R2XYH'
      );
      
      setSubmitStatus({ 
        type: 'success', 
        message: '¡Mensaje enviado correctamente! Te contactaremos pronto.' 
      });
      formRef.current.reset();
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Error al enviar el mensaje. Por favor intenta de nuevo.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-32 relative" data-testid="contact-section">
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-16">
          <AnimatedSection>
            <motion.p variants={fadeInUp} className="text-sm font-mono tracking-widest text-purple-400 mb-4 uppercase">
              Contacto
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              ¿Listo para <span className="gradient-text">Innovar</span>?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-400 text-lg leading-relaxed mb-10">
              Cuéntanos sobre tu proyecto y te ayudaremos a hacerlo realidad. 
              Nuestro equipo está listo para transformar tu visión en una solución digital excepcional.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Email</div>
                  <a href="mailto:innovaappsolutions@gmail.com" className="text-white hover:text-purple-400 transition-colors">innovaappsolutions@gmail.com</a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Teléfono</div>
                  <a href="tel:+573107398319" className="text-white hover:text-cyan-400 transition-colors">+57 310 7398319</a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Ubicación</div>
                  <a href="https://www.google.com/maps/search/?api=1&query=Calle+32+76-22+Medellin+Antioquia+Colombia" target="_blank" rel="noopener noreferrer" className="text-white hover:text-green-400 transition-colors">Calle 32 #76-22, Medellín, Antioquia - Colombia</a>
                </div>
              </div>
            </motion.div>
          </AnimatedSection>

          <AnimatedSection>
            <motion.form 
              ref={formRef}
              onSubmit={handleSubmit}
              variants={fadeInUp}
              className="glass-card p-8 space-y-6"
              data-testid="contact-form"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Nombre</label>
                  <input 
                    type="text" 
                    name="name"
                    placeholder="Tu nombre" 
                    className="glass-input"
                    data-testid="contact-name"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="tu@email.com" 
                    className="glass-input"
                    data-testid="contact-email"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Asunto</label>
                <input 
                  type="text" 
                  name="title"
                  placeholder="¿En qué podemos ayudarte?" 
                  className="glass-input"
                  data-testid="contact-subject"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Mensaje</label>
                <textarea 
                  rows={5}
                  name="message"
                  placeholder="Cuéntanos sobre tu proyecto..." 
                  className="glass-input resize-none"
                  data-testid="contact-message"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {submitStatus.message && (
                <div className={`p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {submitStatus.message}
                </div>
              )}
              
              <button 
                type="submit" 
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="contact-submit"
                disabled={isSubmitting}
              >
                <span>{isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}</span>
                {!isSubmitting && <Send className="w-5 h-5" />}
              </button>
            </motion.form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            {/* Footer Logo */}
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-600 via-purple-500 to-cyan-400 p-[2px]">
              <div className="w-full h-full rounded-[10px] bg-[#0a0a1a] flex items-center justify-center">
                <div className="relative">
                  <Globe className="w-5 h-5 text-cyan-400" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full" />
                </div>
              </div>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-bold text-lg tracking-tight">INNOVA <span className="text-purple-400">APP</span></span>
              <span className="text-[10px] font-medium tracking-[0.2em] text-cyan-400 mt-0.5">SOLUTIONS</span>
            </div>
          </div>
          
          <p className="text-slate-500 text-sm">
            © 2026 INNOVA APP SOLUTIONS. Todos los derechos reservados.
          </p>
          
          <div className="flex gap-6">
            <a href="#" className="text-slate-400 hover:text-white transition-colors">Facebook</a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors">Instagram</a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors">TikTok</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/573107398319?text=Hola%2C%20me%20interesa%20conocer%20más%20sobre%20sus%20servicios"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-24 right-5 md:bottom-6 md:right-6 z-[9999] w-16 h-16 md:w-14 md:h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300"
      style={{ boxShadow: '0 4px 25px rgba(37,211,102,0.6)' }}
      data-testid="whatsapp-button"
      aria-label="Contactar por WhatsApp"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill="white" 
        className="w-8 h-8 md:w-7 md:h-7"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Main App Component
function App() {
  return (
    <div className="relative w-full min-h-screen overflow-x-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <Navbar />
      
      {/* Main Content */}
      <main className="w-full">
        <HeroSection />
        <ServicesSection />
        <PortfolioSection />
        <AboutSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
}

export default App;
